/**
 * Barra de envío gratis del cajón del carrito.
 *
 * El valor lo pinta Liquid en el servidor; esto solo lo recalcula cuando el
 * carrito cambia sin recargar. Hacerlo así y no todo en JS evita el parpadeo
 * de abrir el cajón y ver «te faltan $0» durante un fotograma.
 *
 * Se escucha el mismo evento que consume el icono del carrito de Horizon, con
 * su mismo contrato: el evento llega antes de que la petición termine y trae
 * una promesa con el carrito ya actualizado.
 */

import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Escribe la cifra igual que Liquid.
 *
 * `formatMoney` de Horizon replica los filtros `money` de Liquid con el
 * formato real de la tienda. Se usa ése y no `Intl.NumberFormat` porque los
 * dos no coinciden: para pesos colombianos, Intl escribe «$ 60.000» con
 * espacio y Shopify «$60.000» sin él. Eso se veía al cambiar el carrito —la
 * misma cifra cambiaba de aspecto a mitad de frase.
 *
 * Los ceros de los centavos se quitan al final, porque en el servidor la
 * primera pintada usa `money_without_trailing_zeros`.
 *
 * @param {number} centavos - Importe en unidades menores.
 * @param {Element} barra - De donde salen el formato y la moneda.
 */
function formatear(centavos, barra) {
  const moneda = barra.dataset.currency || window.Shopify?.currency?.active || 'COP';
  const formato = barra.dataset.moneyFormat || '${{amount_no_decimals_with_comma_separator}}';

  try {
    return formatMoney(centavos, formato, moneda).replace(/[.,]00$/, '');
  } catch {
    return `$${Math.round(centavos / 100).toLocaleString('es-CO')}`;
  }
}

/** @param {Element} barra @param {number} total */
function pintarUna(barra, total) {
  const umbral = Number(barra.dataset.threshold) || 0;
  if (!umbral) return;

  const falta = umbral - total;
  const logrado = falta <= 0;

  barra.classList.toggle('drikvar-ship--ok', logrado);

  // Los dos textos viajan en el propio elemento: son texto del comerciante y
  // pueden estar traducidos, así que no se pueden escribir aquí.
  //
  // La cifra va en el hueco [monto], igual que en el servidor. Pegarla al
  // final deja media frase —«te faltan $60.000» no dice para qué—, y en otros
  // idiomas el importe ni siquiera va al final. Sin hueco, se añade detrás,
  // que es como se comportaba antes.
  const texto = barra.querySelector('[data-drikvar-ship-text]');
  if (texto) {
    const plantilla = barra.dataset.pending || '';
    const monto = formatear(falta, barra);

    texto.textContent = logrado
      ? barra.dataset.done
      : plantilla.includes('[monto]')
        ? plantilla.replace('[monto]', monto)
        : `${plantilla} ${monto}`.trim();
  }

  const relleno = barra.querySelector('[data-drikvar-ship-fill]');
  if (relleno) relleno.style.width = `${Math.min(100, (total / umbral) * 100)}%`;
}

// En plural: la página del carrito tiene su propia barra y el cajón la suya,
// y las dos pueden estar montadas a la vez.
function pintar(total) {
  document.querySelectorAll('[data-drikvar-ship]').forEach((barra) => pintarUna(barra, total));
}

document.addEventListener(StandardEvents.cartLinesUpdate, (evento) => {
  evento.promise
    ?.then(({ cart }) => {
      // `total_price` viene de /cart.js; `cost.totalAmount` de la API nueva.
      const total = cart?.total_price ?? cart?.cost?.totalAmount?.amount * 100;
      if (typeof total === 'number' && !Number.isNaN(total)) pintar(total);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[drikvar-ship]', error);
    });
});
